import { createTheme } from '@mui/material/styles';
import {
  brandColor,
  cta,
  customText,
  fontFamily,
  fontFamilyMono,
  outline,
  radius,
  scrollbar,
  shadow,
  statusColor,
  tableToken,
  userAvatarColor,
  warmNeutral,
  wordmark,
} from './tokens';

/** v3.9 Pattern A rail — widths + grouped-item styling. Colors are functions
 *  (matching `roommatik.console.ui`'s reference theme convention) so a
 *  future dark-mode variant could branch on the ambient theme; this app is
 *  light-mode only today. */
interface MenuItemStateStyle {
  color: () => string;
  backgroundColor: () => string;
}

interface MenuItemActiveStyle {
  color: () => string;
  backgroundColor: () => string;
  fontWeight: number;
  marker: () => string;
  hover: { backgroundColor: () => string };
}

interface MenuItemClosedActiveIconStyle {
  backgroundColor: () => string;
  color: () => string;
  borderRadius: string;
}

interface MenuItemStyle {
  borderRadius: number;
  margin: string;
  padding: string;
  gap: string;
  fontSize: string;
  fontWeight: number;
  color: () => string;
  iconColor: () => string;
  hover: MenuItemStateStyle;
  active: MenuItemActiveStyle;
  closedActiveIcon: MenuItemClosedActiveIconStyle;
}

interface MenuThemeConfig {
  openWidth: string;
  closedWidth: string;
  border: string;
  groupLabelColor: string;
  item: MenuItemStyle;
}

declare module '@mui/material/styles' {
  interface TypeBackground {
    /** v3.1 — the app-bar surface (distinct from `default`/`paper`). */
    appbar: string;
    /** v3.1 — the rail surface (a desaturated teal, not the brand accent). */
    menu: string;
  }

  /** react-admin's own `<Sidebar>` reads this to size its Drawer (v3.9 Pattern A rail). */
  interface Theme {
    sidebar: {
      width: number;
      closedWidth: number;
    };
  }
  interface ThemeOptions {
    sidebar?: {
      width?: number;
      closedWidth?: number;
    };
  }

  interface Theme {
    custom: {
      canvasBackground: string;
      cardBorder: string;
      cardShadow: string;
      popoverShadow: string;
      radiusXs: number;
      wordmarkBrandColor: string;
      wordmarkProductColor: string;
      fontFamilyMono: string;
      /** v3.8 solid-button fill (teal-ink) — shared by MuiButton (contained). */
      ctaFill: string;
      ctaFillHover: string;
      /** v3.2 outline-system knobs. */
      borderStrong: string;
      borderRow: string;
      borderCard: string;
      /** v3 micro-tones not modeled by text.primary/secondary. */
      text: {
        tertiary: string;
        label: string;
        idMono: string;
      };
      /** v3.8bis neutral user-avatar disc. */
      userAvatar: {
        bg: string;
        color: string;
      };
      /** v3.4 unified datagrid header — single source of truth. */
      table: {
        headerBg: string;
        headCell: {
          fontSize: string;
          fontWeight: number;
          lineHeight: number;
          textTransform: 'none';
          color: string;
        };
      };
      /** v3.9 Pattern A rail — widths + grouped-item styling. */
      menu: MenuThemeConfig;
    };
  }
  interface ThemeOptions {
    custom?: {
      canvasBackground?: string;
      cardBorder?: string;
      cardShadow?: string;
      popoverShadow?: string;
      radiusXs?: number;
      wordmarkBrandColor?: string;
      wordmarkProductColor?: string;
      fontFamilyMono?: string;
      ctaFill?: string;
      ctaFillHover?: string;
      borderStrong?: string;
      borderRow?: string;
      borderCard?: string;
      text?: {
        tertiary?: string;
        label?: string;
        idMono?: string;
      };
      userAvatar?: {
        bg?: string;
        color?: string;
      };
      table?: {
        headerBg?: string;
        headCell?: {
          fontSize?: string;
          fontWeight?: number;
          lineHeight?: number;
          textTransform?: 'none';
          color?: string;
        };
      };
      menu?: Partial<MenuThemeConfig>;
    };
  }
}

declare module '@mui/material/Chip' {
  interface ChipPropsVariantOverrides {
    /** Intense solid-accent chip — a single per-view emphasis pill (v3.2).
     *  Never for repeated status tints in dense tables. */
    solid: true;
  }
}

/**
 * "3c Hybrid" v3 theme — the current direction for first-party internal /
 * back-office tools (`docs/design-system/web/hybrid-3c.md`). Presentation
 * only: warm-neutral palette (v3.1), outline system (v3.2), typography
 * (v3.3), unified datagrid header (v3.4), buttons (v3.8), light mode only.
 */
export const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: brandColor.signalTeal,
      dark: brandColor.deepTeal,
      light: brandColor.brandTeal,
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: warmNeutral.grey[500],
      light: warmNeutral.grey[400],
      dark: warmNeutral.grey[600],
      contrastText: '#FFFFFF',
    },
    background: {
      default: warmNeutral.canvas,
      paper: warmNeutral.paper,
      appbar: warmNeutral.appbar,
      menu: warmNeutral.rail,
    },
    text: {
      primary: warmNeutral.ink,
      secondary: warmNeutral.textSecondary,
    },
    divider: warmNeutral.divider,
    success: { main: statusColor.success },
    error: { main: statusColor.error },
    info: { main: statusColor.info },
    warning: { main: statusColor.warning },
    grey: warmNeutral.grey,
  },
  typography: {
    fontFamily,
    h1: { fontSize: '1.625rem', fontWeight: 600, lineHeight: 1.3, letterSpacing: '-0.02em' },
    h2: { fontSize: '1.375rem', fontWeight: 600, lineHeight: 1.3, letterSpacing: '-0.015em' },
    h3: { fontSize: '1.1875rem', fontWeight: 600, lineHeight: 1.35, letterSpacing: '-0.01em' },
    h4: { fontSize: '1.0625rem', fontWeight: 500, lineHeight: 1.4 },
    h5: { fontSize: '1rem', fontWeight: 500, lineHeight: 1.45 },
    h6: { fontSize: '0.9375rem', fontWeight: 500, lineHeight: 1.45, letterSpacing: '0.01em' },
    body1: { fontSize: '1rem', fontWeight: 400, lineHeight: 1.6 },
    body2: { fontSize: '0.875rem', fontWeight: 400, lineHeight: 1.55 },
    caption: { fontSize: '0.8125rem', fontWeight: 400, lineHeight: 1.5 },
    button: { textTransform: 'none', fontWeight: 500, letterSpacing: '0.01em' },
  },
  shape: {
    borderRadius: radius.md,
  },
  // v3.9 Pattern A rail — react-admin's <Sidebar> reads `sidebar.width`/
  // `closedWidth` (via lodashGet) to size its Drawer; must match
  // `custom.menu.openWidth`/`closedWidth` below exactly.
  sidebar: {
    width: 230,
    closedWidth: 64,
  },
  custom: {
    canvasBackground: warmNeutral.canvas,
    cardBorder: outline.borderCard,
    cardShadow: shadow.card,
    popoverShadow: shadow.popover,
    radiusXs: radius.xs,
    wordmarkBrandColor: wordmark.brand,
    wordmarkProductColor: wordmark.product,
    fontFamilyMono,
    ctaFill: cta.fill,
    ctaFillHover: cta.fillHover,
    borderStrong: outline.borderStrong,
    borderRow: outline.borderRow,
    borderCard: outline.borderCard,
    text: {
      tertiary: customText.tertiary,
      label: customText.label,
      idMono: customText.idMono,
    },
    userAvatar: {
      bg: userAvatarColor.bg,
      color: userAvatarColor.color,
    },
    table: {
      headerBg: tableToken.headerBg,
      headCell: tableToken.headCell,
    },
    // v3.9 Pattern A rail (hybrid-3c.md §v3.9 / v2 §5.1): widths match
    // `sidebar` above; item colors are translucent-white-on-teal (the rail
    // surface itself, `palette.background.menu`, already carries the teal —
    // these are overlay tones, not new hex).
    menu: {
      openWidth: '230px',
      closedWidth: '64px',
      border: 'rgba(0,0,0,0.08)',
      groupLabelColor: 'rgba(255,255,255,0.55)',
      item: {
        borderRadius: radius.sm,
        margin: '2px 0',
        padding: '7px 8px',
        gap: '12px',
        fontSize: '14px',
        fontWeight: 500,
        color: () => 'rgba(255,255,255,0.85)',
        iconColor: () => 'rgba(255,255,255,0.7)',
        hover: {
          color: () => '#FFFFFF',
          backgroundColor: () => 'rgba(255,255,255,0.08)',
        },
        active: {
          color: () => '#FFFFFF',
          backgroundColor: () => 'rgba(255,255,255,0.16)',
          fontWeight: 600,
          marker: () => '#FFFFFF',
          hover: {
            backgroundColor: () => 'rgba(255,255,255,0.22)',
          },
        },
        closedActiveIcon: {
          backgroundColor: () => 'rgba(255,255,255,0.16)',
          color: () => '#FFFFFF',
          borderRadius: '6px',
        },
      },
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: { backgroundColor: warmNeutral.canvas },
        '*::-webkit-scrollbar': { width: 10, height: 10 },
        '*::-webkit-scrollbar-track': { backgroundColor: scrollbar.track },
        '*::-webkit-scrollbar-thumb': {
          backgroundColor: scrollbar.thumb,
          borderRadius: radius.pill,
        },
        '*::-webkit-scrollbar-thumb:hover': { backgroundColor: scrollbar.thumbHover },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        outlined: {
          borderColor: outline.borderCard,
        },
      },
    },
    // v3.8 — one single MuiButton system: contained = teal-ink fill + white
    // text; outlined = white fill + divider border + dark text (not MUI's
    // transparent outlined). Flat, radius medium, ~38px, padding 8px 14px.
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: radius.medium,
          textTransform: 'none',
          minHeight: '38px',
          padding: '8px 14px',
          boxShadow: 'none',
          '&:hover': { boxShadow: 'none' },
        },
        contained: {
          backgroundColor: cta.fill,
          color: '#FFFFFF',
          boxShadow: 'none',
          '&:hover': { backgroundColor: cta.fillHover, boxShadow: 'none' },
        },
        outlined: {
          backgroundColor: '#FFFFFF',
          borderColor: warmNeutral.divider,
          color: warmNeutral.ink,
          '&:hover': {
            backgroundColor: warmNeutral.grey[100],
            borderColor: warmNeutral.divider,
          },
        },
      },
    },
    // v3.2 chip "warm outline" system — neutral fill + borderStrong border;
    // coloured states = light colour wash + border/text of the same hue; the
    // `solid` variant is the one-per-view intense accent.
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: radius.pill,
          fontWeight: 500,
          backgroundColor: '#F1EDE6',
          color: '#2C2926',
          border: `1px solid ${outline.borderStrong}`,
        },
        colorSecondary: {
          backgroundColor: '#F1EDE6',
          color: '#2C2926',
          border: `1px solid ${outline.borderStrong}`,
        },
      },
      variants: [
        {
          props: { variant: 'solid' },
          style: {
            border: 'none',
            backgroundColor: brandColor.signalTeal,
            color: '#FFFFFF',
            fontWeight: 500,
          },
        },
      ],
    },
    // v3.2a — white fill + 1px light sand border + soft warm shadow (card
    // definition comes from the shadow, not a dark hairline).
    MuiCard: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          backgroundColor: warmNeutral.paper,
          borderRadius: radius.md,
          boxShadow: shadow.card,
          border: `1px solid ${outline.borderCard}`,
        },
      },
    },
    // v3.4 — unified datagrid header: headCell token is the single source of
    // truth; header borderBottom = borderStrong, rows = borderRow.
    RaDatagrid: {
      styleOverrides: {
        root: {
          backgroundColor: warmNeutral.paper,
          border: `1px solid ${warmNeutral.grey[200]}`,
          borderRadius: radius.md,
          overflow: 'hidden',

          '& .RaDatagrid-headerCell': {
            backgroundColor: tableToken.headerBg,
            borderBottom: `1px solid ${outline.borderStrong}`,
            padding: '12px',
            ...tableToken.headCell,
          },

          '& .RaDatagrid-rowCell': {
            borderBottom: `1px solid ${outline.borderRow}`,
            color: warmNeutral.ink,
          },

          'tbody tr:last-child .RaDatagrid-rowCell': {
            borderBottom: 'none',
          },
        },
      },
    },
  },
});
